import { useCommonEntitiesStore } from "@/States/commonEntities";

import React, { useEffect, useMemo, useState } from "react";

import { HelpSection } from "./HelpSection";
import { IImportContext, ImportContext } from "./ImportContext";
import { MassImportButtons } from "./components/MassImportButtons";
import { StepViewer } from "./components/StepViewer";
import { useImportState } from "./importState";
import { ImportStep, Template } from "./types";

type Props = {
  steps: ImportStep[];
  templates: Template[];
  addToSystem: (context: IImportContext) => void;
  onCancel: () => void;
};

export const ImportView: React.FC<Props> = ({
  steps,
  templates,
  addToSystem,
  onCancel,
}: Props) => {
  const [activeStep, setActiveStep] = useState<ImportStep>(steps[0]);
  const importState = useImportState();
  const { helpSections } = useCommonEntitiesStore();

  const stepKeys = useMemo(() => steps.map((step) => step.key), [steps]);

  useEffect(() => {
    // reset on opening import view
    importState.fullReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  const description = useMemo(() => {
    if (activeStep.hideDescription) {
      return null;
    }

    if (helpSections) {
      return (
        <HelpSection activeStep={activeStep} helpSections={helpSections} />
      );
    }
  }, [activeStep, helpSections]);

  const activeStepView = useMemo(() => activeStep.view(), [activeStep]);

  return (
    <ImportContext.Provider
      value={{
        steps,
        activeStep,
        setActiveStep,
        templates,
        addToSystem,
        onCancel,
        ...importState,
      }}
    >
      <div className="flex w-full flex-col">
        <div className="relative h-fit w-full flex-row items-baseline gap-4 bg-white px-5 pt-4 pb-3">
          <StepViewer stepKeys={stepKeys} activeStepKey={activeStep.key} />
        </div>
        <div className="relative mt-[2px] h-full w-full overflow-scroll bg-white px-5 pt-5">
          {description}
          {activeStepView}
        </div>
        {!activeStep.hideButtons && <MassImportButtons />}
      </div>
    </ImportContext.Provider>
  );
};
